import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { client } from "@/apis/gql/apolloClient";
import { useTenant } from "@/providers/session";

import { GET_ALL_EMAIL_FORWARDINGS } from "./get-all";

type UpdateEmailForwardingInput = {
  emailForwardingId: string;
  isActive?: boolean;
  alias?: string;
  description?: string;
};

export const useUpdateEmailForwarding = () => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();

  const [updateEmailForwarding, { data, loading, error }] = useMutation(
    UPDATE_EMAIL_FORWARDING_MUTATION,
    {
      optimisticResponse: (vars: any) => {
        const cache = client.cache;
        const existingForwarding = cache.readFragment({
          id: `EmailForwarding:${vars.emailForwardingId}`,
          fragment: gql`
            fragment ExistingEmailForwarding on EmailForwarding {
              emailForwardingId
              forwardingAddress
              isActive
              alias
              description
              createdAt
              updatedAt
            }
          `,
        });

        if (!existingForwarding) {
          return {
            updateEmailForwarding: null,
          };
        }

        const updates = {
          isActive: vars.isActive,
          alias: vars.alias,
          description: vars.description,
        };

        return {
          updateEmailForwarding: {
            __typename: "EmailForwarding",
            ...existingForwarding,
            ...updates,
          },
        };
      },
      update: (cache, { data: { updateEmailForwarding } }) => {
        if (updateEmailForwarding) {
          cache.modify({
            id: cache.identify(updateEmailForwarding),
            fields: {
              isActive: () => updateEmailForwarding.isActive,
              alias: () => updateEmailForwarding.alias,
              description: () => updateEmailForwarding.description,
            },
          });
        }
      },
      refetchQueries: [GET_ALL_EMAIL_FORWARDINGS],
    },
  );

  const handleUpdateEmailForwarding = async (
    data: UpdateEmailForwardingInput,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    const toastId = toast.loading(t("generic_loading"));

    try {
      const response = await updateEmailForwarding({
        variables: {
          ...data,
          tenantId,
        },
      });

      toast.success(t("generic_success"), { id: toastId });
      onSuccess();
      return response;
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      onError();
      throw error;
    }
  };

  return {
    updateEmailForwarding: handleUpdateEmailForwarding,
    data: data?.updateEmailForwarding,
    loading,
    error,
  };
};

export const UPDATE_EMAIL_FORWARDING_MUTATION = gql`
  mutation UpdateEmailForwarding(
    $tenantId: String!
    $emailForwardingId: String!
    $isActive: Boolean
    $alias: String
    $description: String
  ) {
    updateEmailForwarding(
      tenantId: $tenantId
      emailForwardingId: $emailForwardingId
      isActive: $isActive
      alias: $alias
      description: $description
    ) {
      emailForwardingId
      forwardingAddress
      isActive
      alias
      description
      createdAt
      updatedAt
    }
  }
`;
